import { NETWORK_ERROR_PATTERNS_LOWERCASE } from '../constants/networkErrorPatterns';

/**
 * Component error types for error boundary categorization
 * Used for categorizing and tracking application errors
 */
export enum ComponentErrorType {
  /** Network/fetch related errors */
  NETWORK = 'network',

  /** React rendering errors (hooks, lifecycle, etc.) */
  RENDER = 'render',

  /** State management errors (Zustand, React Query, etc.) */
  STATE = 'state',

  /** Code splitting/lazy loading chunk failures */
  CHUNK_LOAD = 'chunk_load',

  /** Authentication/authorization errors */
  AUTHENTICATION = 'authentication',

  /** Any other unhandled error */
  GENERIC = 'generic',
}

/**
 * Categorizes a component error into a trackable error type
 * Checks error messages and names for specific patterns
 *
 * @param error - The error object caught by error boundary
 * @returns The categorized error type
 */
export function categorizeComponentError(error: Error): ComponentErrorType {
  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  // Check for chunk loading errors (dynamic imports, code splitting)
  if (
    message.includes('loading chunk') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('dynamically imported module') ||
    name.includes('chunkerror') ||
    name.includes('chunkload')
  ) {
    return ComponentErrorType.CHUNK_LOAD;
  }

  // Check for authentication errors
  if (
    message.includes('unauthorized') ||
    message.includes('authentication') ||
    message.includes('not authenticated') ||
    message.includes('auth token') ||
    message.includes('access token') ||
    message.includes('refresh token') ||
    message.includes('token expired') ||
    message.includes('invalid token') ||
    message.includes('session expired')
  ) {
    return ComponentErrorType.AUTHENTICATION;
  }

  // Check for network errors
  if (NETWORK_ERROR_PATTERNS_LOWERCASE.some(pattern => message.includes(pattern))) {
    return ComponentErrorType.NETWORK;
  }

  // Check for React rendering errors
  if (
    message.includes('rendered') ||
    message.includes('hook') ||
    message.includes('component') ||
    message.includes('unmounted') ||
    name.includes('invariant')
  ) {
    return ComponentErrorType.RENDER;
  }

  // Check for state management errors
  if (
    message.includes('state') ||
    message.includes('store') ||
    message.includes('query client') ||
    message.includes('query cache') ||
    message.includes('mutation')
  ) {
    return ComponentErrorType.STATE;
  }

  return ComponentErrorType.GENERIC;
}

/**
 * Creates a sanitized hash of the component stack for analytics
 * Returns first component name from stack for lightweight tracking
 *
 * @param componentStack - The React component stack string
 * @returns Sanitized component identifier (first component name or 'unknown')
 */
export function getErrorHash(componentStack?: string): string {
  if (!componentStack) {
    return 'unknown';
  }

  // Extract first component name from stack (e.g., "at ComponentName" or "in ComponentName")
  const match = /(?:at|in)\s+([A-Z][A-Za-z0-9_]*)/.exec(componentStack);
  if (match?.[1]) {
    return match[1];
  }

  return 'unknown';
}
